import { ActionFunction } from '../interface/action/action-function.type';
import { ActionEnum } from '../enum/action.enum';
import { action } from 'mobx';
import { CustomListDefinitionModel } from '../model/custom-lists/custom-list-definition.model';
import { getUniqueId } from '../util/string/get-unique-id.util';
import { AjaxResponse, Observable } from 'rxjs';
import { findIndex } from 'lodash';
import { IRoutePayload } from '../interface/action/route-payload.interface';
import { convertPathToRegEx } from '../util/route/convert-path-to-regex';
import { loadCustomListDefinition } from '../util/action/type/load-custom-list-definition-action.util';
import { joinUrl } from '../util/url/join-url.util';
import { redirectPage } from '../util/action/type/redirect-page.util';
import { loadCustomListDefinitionEntries } from '../util/action/type/load-custom-list-definition-entries-action.util';
import { IAction } from '../interface/action/action.interface';
import { getUrlSafeString } from '../util/string/get-url-safe-string.util';
import { CustomListEntryModel } from '../model/custom-lists/custom-list-entry.model';
import { loadCustomListEntry } from '../util/action/type/load-custom-list-entry-action.util';
import { noop } from '../util/action/type/noop-action.util';
import {CUSTOM_LIST_SLUG} from '../constants';
import {logDebugEvent} from '../util/debug/log-debug-event.util';

export interface ILoadDynamicCustomListDefinition {
  includeEntries: boolean;
}

export const loadDynamicCustomListDefinition$: ActionFunction<IRoutePayload & ILoadDynamicCustomListDefinition> = (action$) => {
  return action$
    .type(ActionEnum.LOAD_DYNAMIC_CUSTOM_LIST_DEFINITION)
    .map((payload: IRoutePayload & ILoadDynamicCustomListDefinition) => {
      const pathRegEx = convertPathToRegEx(payload.match.path.replace('*', ''));
      const id = payload.location.pathname.replace(pathRegEx, '').split('/')[ 0 ];
      const toReturn: IAction[] = [loadCustomListDefinition(id)];
      if (payload.includeEntries) {
        toReturn.push(loadCustomListDefinitionEntries(id));
      }
      return toReturn;
    });
};

export const loadDynamicCustomListEntry$: ActionFunction<IRoutePayload> = (action$) => {
  return action$
    .type(ActionEnum.LOAD_DYNAMIC_CUSTOM_LIST_ENTRY)
    .map((payload: IRoutePayload) => {
      const pathRegEx = convertPathToRegEx(payload.match.path.replace('*', ''));
      const id = payload.location.pathname.replace(pathRegEx, '').split('/')[ 0 ];
      const defId = payload.location.pathname.replace(pathRegEx, '').split('/')[ 1 ];
      return [loadCustomListDefinition(defId), loadCustomListEntry(id), loadCustomListDefinitionEntries(defId)];
    });
};

export const newCustomListDefinition$: ActionFunction = (action$, {state}) => {
  return action$
    .type(ActionEnum.NEW_CUSTOM_LIST_DEFINITION)
    .end(action(() => {
      const newCustomListDefinition = new CustomListDefinitionModel();
      newCustomListDefinition.id = getUniqueId();
      const name = 'New Custom List Definition';
      newCustomListDefinition.name = name;
      newCustomListDefinition.isSaved = false;
      state.editor.editingCustomListDefinition = newCustomListDefinition;
    }));
};

export const newCustomListEntry$: ActionFunction<IRoutePayload> = (action$, {state}) => {
  return action$
    .type(ActionEnum.NEW_CUSTOM_LIST_ENTRY)
    .end(action((payload: IRoutePayload) => {
      const newCustomListEntry = new CustomListEntryModel();
      newCustomListEntry.id = getUniqueId();
      const name = 'New Custom List Entry';
      newCustomListEntry.title = name;
      newCustomListEntry.slug = getUrlSafeString(name);
      newCustomListEntry.isSaved = false;
      const pathRegEx = convertPathToRegEx(payload.match.path.replace('*', ''));
      const customListDefinitionId = payload.location.pathname.replace(pathRegEx, '').split('/')[ 0 ];
      newCustomListEntry.customListDefinitionId = customListDefinitionId;
      state.editor.editingCustomListEntry = newCustomListEntry;
    }));
};

export interface ISaveCustomListPayload {
  listDefinition: CustomListDefinitionModel;
}

export const saveCustomListDefinition$: ActionFunction<ISaveCustomListPayload> = (action$, {state, service}) => {
  return action$
    .type(ActionEnum.SAVE_CUSTOM_LIST_DEFINITION)
    .switchMap(action((payload: ISaveCustomListPayload) => {
      state.isLoading = true;
      return service.customListDefinition.saveCustomListDefinition(payload.listDefinition)
        .map(action((response: AjaxResponse) => {
          state.isLoading = false;
          payload.listDefinition.id = response.response.id;
          payload.listDefinition.isSaved = true;
          state.editor.editingCustomListDefinition = payload.listDefinition;
          return redirectPage(joinUrl(`/admin/${CUSTOM_LIST_SLUG}/edit-definition`, response.response.id));
        }));
    }));
};

export interface ISaveCustomListEntryPayload {
  entry: CustomListEntryModel;
}

export const saveCustomListEntry$: ActionFunction<ISaveCustomListEntryPayload> = (action$, {state, service}) => {
  return action$
    .type(ActionEnum.SAVE_CUSTOM_LIST_ENTRY)
    .switchMap(action((payload: ISaveCustomListEntryPayload) => {
      state.isLoading = true;
      return service.customListEntryService.saveEntry(payload.entry)
        .map(action((response: AjaxResponse) => {
          state.isLoading = false;
          payload.entry.id = response.response.id;
          payload.entry.isSaved = true;
          state.editor.editingCustomListEntry = payload.entry;
          return redirectPage(joinUrl(`/admin/${CUSTOM_LIST_SLUG}/definition`, payload.entry.customListDefinitionId));
        }));
    }));
};

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const loadCustomListDefinitions$: ActionFunction = (action$, {state, service, logger}) => {
  return action$
    .type(ActionEnum.LOAD_CUSTOM_LIST_DEFINITIONS)
    .switchMap(action(() => {
      if (!state.configuration.authorization.cmsAdvancedContentUse) {
        return Observable.of(noop());
      }
      state.isLoading = true;
      logDebugEvent(logger, 'CMS-SSR LOAD_CUSTOM_LIST_DEFINITIONS entry');
      return service.customListDefinition.getAllLists()
        .do(action((response: AjaxResponse) => {
          state.isLoading = false;
          state.editor.customListDefinitions = response.response;
          logDebugEvent(logger, 'CMS-SSR LOAD_CUSTOM_LIST_DEFINITIONS end');
        }));
    }));
};

export interface ILoadCustomListDefinitionEntriesPayload {
  definitionId: string;
}

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const loadCustomListDefinitionEntries$: ActionFunction<ILoadCustomListDefinitionEntriesPayload> = (action$, {state, service}) => {
  return action$
    .type(ActionEnum.LOAD_CUSTOM_LIST_DEFINITION_ENTRIES)
    .switchMap(action((payload: ILoadCustomListDefinitionEntriesPayload) => {
      if (!state.configuration.authorization.cmsAdvancedContentUse) {
        return Observable.of(noop());
      }
      state.isLoading = true;
      return service.customListEntryService.getCustomListEntriesForDefinition(payload.definitionId)
        .do(action((response: AjaxResponse) => {
          state.isLoading = false;
          state.editor.customListDefinitionEntries = response.response;
        }));
    }));
};

export interface ILoadCustomListDefinition {
  id: string;
}

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const loadCustomListDefinition$: ActionFunction<ILoadCustomListDefinition> = (action$, {state, service}) => {
  return action$
    .type(ActionEnum.LOAD_CUSTOM_LIST_DEFINITION)
    .switchMap(action((payload: ILoadCustomListDefinition) => {
      if (!state.configuration.authorization.cmsAdvancedContentUse) {
        return Observable.of(noop());
      }
      state.isLoading = true;
      return service.customListDefinition.getListById(payload.id)
        .do(action((response: AjaxResponse) => {
          state.isLoading = false;
          state.editor.editingCustomListDefinition = new CustomListDefinitionModel();
          state.editor.editingCustomListDefinition.setCustomListDefinitionData(response.response);
        }));
    }));
};

export interface ILoadCustomListEntry {
  id: string;
}

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const loadCustomListEntry$: ActionFunction<ILoadCustomListEntry> = (action$, {state, service}) => {
  return action$
    .type(ActionEnum.LOAD_CUSTOM_LIST_ENTRY)
    .switchMap(action((payload: ILoadCustomListEntry) => {
      if (!state.configuration.authorization.cmsAdvancedContentUse) {
        return Observable.of(noop());
      }
      state.isLoading = true;
      return service.customListEntryService.getListEntryById(payload.id)
        .do(action((response: AjaxResponse) => {
          state.isLoading = false;
          state.editor.editingCustomListEntry = new CustomListEntryModel();
          state.editor.editingCustomListEntry.setCustomListEntryData(response.response);
        }));
    }));
};

export interface IDeleteCustomListDefinitionPayload {
  customListId: string;
}

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const deleteCustomListDefinition$: ActionFunction<IDeleteCustomListDefinitionPayload> = (action$, {state, service}) => {
  return action$
    .type(ActionEnum.DELETE_CUSTOM_LIST_DEFINITION)
    .switchMap(action((payload: IDeleteCustomListDefinitionPayload) => {
      state.isLoading = true;
      return service.customListDefinition.deleteList(payload.customListId)
        .do(action(() => {
          state.isLoading = false;
          if (state.editor.editingCustomListDefinition && state.editor.editingCustomListDefinition.id === payload.customListId) {
            state.editor.editingCustomListDefinition = null;
          }
          const definitionIndex = findIndex(state.editor.customListDefinitions, {id: payload.customListId});
          if (definitionIndex !== -1) {
            state.editor.customListDefinitions.splice(definitionIndex, 1);
          }
        }));
    }));
};

export interface IDeleteCustomListEntryPayload {
  customListEntryId: string;
}

// TODO2024 - remove @ts-ignore
// @ts-ignore
export const deleteCustomListEntry$: ActionFunction<IDeleteCustomListEntryPayload> = (action$, {state, service}) => {
  return action$
    .type(ActionEnum.DELETE_CUSTOM_LIST_ENTRY)
    .switchMap(action((payload: IDeleteCustomListEntryPayload) => {
      state.isLoading = true;
      return service.customListEntryService.deleteEntry(payload.customListEntryId)
        .do(action(() => {
          state.isLoading = false;
          if (state.editor.editingCustomListEntry && state.editor.editingCustomListEntry.id === payload.customListEntryId) {
            state.editor.editingCustomListDefinition = null;
          }
          const entryIndex = findIndex(state.editor.customListDefinitionEntries, {id: payload.customListEntryId});
          if (entryIndex !== -1) {
            state.editor.customListDefinitionEntries.splice(entryIndex, 1);
          }
        }));
    }));
};

export default {
  actions: [
    newCustomListDefinition$,
    newCustomListEntry$,
    saveCustomListDefinition$,
    loadCustomListDefinitions$,
    loadDynamicCustomListDefinition$,
    loadDynamicCustomListEntry$,
    deleteCustomListDefinition$,
    deleteCustomListEntry$,
    loadCustomListDefinition$,
    loadCustomListDefinitionEntries$,
    saveCustomListEntry$,
    loadCustomListEntry$,
  ]
};
